import React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { BriefChatMessage } from '../../../common/hooks/use-decisions-api.hook';

/**
 * The brief as it will look where it lands: a WhatsApp or Telegram chat, or
 * the email. The chat is drawn from the API's own message plan — the same
 * messages, buttons and picture `send` posts — and its light markdown
 * (**bold**, [label](url)) is turned into elements here, never into HTML.
 */

const TOKEN = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;

/** **bold** and [label](url) as React nodes; everything else stays text. */
export function lightMarkdown(text: string, linkClass = 'underline underline-offset-2'): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let key = 0;
  const re = new RegExp(TOKEN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    const at = m.index;
    if (at > last) out.push(text.slice(last, at));
    if (m[1]) out.push(<strong key={key++} className="font-medium">{m[1]}</strong>);
    else out.push(<a key={key++} href={m[3]} target="_blank" rel="noreferrer" className={linkClass}>{m[2]}</a>);
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type App = 'whatsapp' | 'telegram';

/** Each app's own look, drawn in its own colours: this is a picture of their screen, not of ours. */
const LOOK: Record<App, { wallpaper: string; bubble: string; ink: string; meta: string; button: React.CSSProperties; buttonInk: string }> = {
  whatsapp: { wallpaper: '#efeae2', bubble: '#ffffff', ink: '#111b21', meta: '#667781', button: { background: '#ffffff' }, buttonInk: '#027eb5' },
  telegram: { wallpaper: '#d9e4ec', bubble: '#ffffff', ink: '#0f1419', meta: '#7d8b99', button: { background: 'rgba(53, 79, 102, 0.22)' }, buttonInk: '#ffffff' },
};

export const ChatPreview: React.FC<{ app: App; messages: BriefChatMessage[]; channelName?: string; time?: string }> = ({ app, messages, channelName, time = '07:30' }) => {
  const { t } = useTranslation();
  const look = LOOK[app];
  return (
    <div className="overflow-hidden rounded-2xl border" data-testid={`brief-preview-${app}`}>
      <div className="flex items-center gap-2.5 border-b bg-card px-3 py-2">
        <span className="flex size-8 items-center justify-center rounded-full bg-foreground text-xs font-medium text-background">A</span>
        <div className="min-w-0 leading-tight">
          <p className="text-sm font-medium">Ardi</p>
          <p className="truncate text-xs text-muted-foreground">{channelName ?? t(`DECISIONS.BRIEF.TAB.${app.toUpperCase()}`)}</p>
        </div>
      </div>
      <div className="flex max-h-[620px] flex-col gap-2 overflow-y-auto px-3 py-4" style={{ background: look.wallpaper }}>
        <span className="mx-auto mb-1 rounded-md bg-white/80 px-2 py-0.5 text-[11px]" style={{ color: look.meta }}>{t('DECISIONS.BRIEF.TODAY')}</span>
        {messages.map((m, i) => (
          <div key={i} className="flex max-w-[88%] flex-col gap-1">
            <div className="rounded-lg rounded-ss-none p-1.5 shadow-sm" style={{ background: look.bubble, color: look.ink }}>
              {m.media?.[0] && <img src={m.media[0].url} alt="" className="mb-1.5 max-h-52 w-full rounded-md object-cover" />}
              <p className="whitespace-pre-wrap break-words px-1.5 text-[13px] leading-snug">{lightMarkdown(m.text, app === 'whatsapp' ? 'text-[#027eb5] underline-offset-2' : 'text-[#168acd]')}</p>
              <p className="px-1.5 pt-0.5 text-end text-[10px]" style={{ color: look.meta }}>{time}</p>
            </div>
            {m.buttons?.map((row, r) => (
              <div key={r} className={cn('flex gap-1', app === 'whatsapp' && 'flex-col')}>
                {row.map((b) => (
                  <span
                    key={b.id}
                    className="flex-1 truncate rounded-lg px-2 py-1.5 text-center text-[13px] font-medium shadow-sm"
                    style={{ ...look.button, color: look.buttonInk }}
                    title={b.id}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/** The email exactly as sent, in a sandbox (no scripts, no navigation), with its subject above. */
export const EmailPreview: React.FC<{ subject: string; html: string }> = ({ subject, html }) => {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl border" data-testid="brief-preview-email">
      <div className="flex flex-col gap-0.5 border-b bg-card px-4 py-3 text-xs">
        <p className="text-muted-foreground">
          {t('DECISIONS.BRIEF.EMAIL_FROM')} <span className="text-foreground">Ardi from Ardealy</span>
        </p>
        <p className="text-muted-foreground">
          {t('DECISIONS.BRIEF.EMAIL_SUBJECT')} <span className="font-medium text-foreground">{subject}</span>
        </p>
      </div>
      <iframe title={subject} srcDoc={html} sandbox="" className="h-[620px] w-full border-0 bg-white" />
    </div>
  );
};
